import { UIState } from "@hydra/core/uiState";
import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class MegaMenu implements Plugin {
  private unsubscribeNavbar?: () => void;
  private unsubscribeHeaderSticked?: () => void;
  private unsubscribeBreakpoint?: () => void;
  private readonly megaMenuWrapper: HTMLDivElement;
  private readonly megaMenuDesktop: HTMLDivElement;
  private readonly megaMenuMobile: HTMLDivElement;
  private readonly megaMenuTabsDesktop: NodeListOf<HTMLDivElement>;
  private readonly mainTab: HTMLElement;
  private readonly tabLink: HTMLAnchorElement;
  private mobileInitialized: boolean = false;
  private readonly isTouchDevice: boolean;

  private readonly originalParent: HTMLElement | null;
  private readonly originalNextSibling: HTMLElement | null;
  private readonly headerNavWrapper: HTMLElement | null;

  private currentTeleportedContent: HTMLElement | null = null;
  private originalContentParent: HTMLElement | null = null;
  private originalContentNextSibling: HTMLElement | null = null;

  private readonly megaMenuOriginalParent: HTMLElement | null;
  private readonly megaMenuOriginalNextSibling: HTMLElement | null;

  private readonly mouseEnterListener: EventListener;
  private readonly mouseLeaveListener: EventListener;
  private readonly clickListener: EventListener;
  private readonly documentClickListener: EventListener;
  private readonly megaMenuMouseEnterListener: EventListener;
  private readonly megaMenuMouseLeaveListener: EventListener;
  private readonly mobileTabClickListener: EventListener;
  private readonly mobileMenuClickListener: EventListener;
  private hideTimeout: number | null = null;

  constructor(private readonly element: HTMLElement) {
    this.mainTab = this.element;
    this.tabLink = this.mainTab.querySelector(
      "a.dsc-co-menu-tab",
    ) as HTMLAnchorElement;

    // Detectar si es dispositivo táctil
    this.isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    this.megaMenuWrapper = this.element.querySelector(
      ".dsc-co-header-nav__megamenu",
    ) as HTMLDivElement;
    this.megaMenuDesktop = this.element.querySelector(
      ".dsc-co-megaMenu",
    ) as HTMLDivElement;
    this.megaMenuMobile = this.element.querySelector(
      ".dsc-co-megaMenu-mobile",
    ) as HTMLDivElement;

    this.megaMenuTabsDesktop = this.megaMenuDesktop.querySelectorAll(
      ".dsc-co-megaMenu__tab",
    );

    this.headerNavWrapper = document.querySelector(
      ".dsc-co-header__nav-wrapper",
    );

    this.originalParent = this.megaMenuMobile.parentElement;
    this.originalNextSibling = this.megaMenuMobile
      .nextElementSibling as HTMLElement;

    this.megaMenuOriginalParent = this.megaMenuWrapper.parentElement;
    this.megaMenuOriginalNextSibling = this.megaMenuWrapper
      .nextElementSibling as HTMLElement;

    this.mouseEnterListener = () => this.showMegaMenu();
    this.mouseLeaveListener = () => this.scheduleMegaMenuHide();
    this.clickListener = (e) => this.handleTouchClick(e);
    this.documentClickListener = (e) => this.handleDocumentClick(e);
    this.megaMenuMouseEnterListener = () => this.cancelMegaMenuHide();
    this.megaMenuMouseLeaveListener = () => this.scheduleMegaMenuHide();
    this.mobileTabClickListener = (e) =>
      this.handleMobileTabClick(e as MouseEvent);
    this.mobileMenuClickListener = (e) => this.handleMobileMenuClick(e);
  }
  init(): void {
    const uiState = UIState.getInstance();
    this.unsubscribeNavbar = uiState.subscribe<boolean>(
      "isNavbarVisible",
      (newValue) => {
        this.updateMegaMenuVisibility(newValue);
      },
    );

    this.unsubscribeHeaderSticked = uiState.subscribe<boolean>(
      "isHeaderSticked",
      (isSticked) => {
        this.updateMegaMenuPosition(isSticked);
      },
    );

    this.unsubscribeBreakpoint = uiState.subscribe<string>(
      "currentBreakpoint",
      () => {
        this.updateMegaMenuVisibility(uiState.isNavbarVisible);
      },
    );

    setTimeout(() => {
      this.initializeMegaMenuStates();

      if (uiState.isNavbarVisible) {
        this.updateMegaMenuVisibility(true);
        this.updateMegaMenuPosition(uiState.isHeaderSticked);

        this.megaMenuTabsDesktop.forEach((tab) => {
          tab.addEventListener("click", (event) =>
            this.handleMegaMenuTabClick(event, tab),
          );
        });
      } else {
        this.initializeMobileMenu();
      }
    }, 1);
  }

  private initializeMegaMenuStates(): void {
    this.megaMenuWrapper.classList.remove(
      "dsc-co-header-nav__megamenu--visible",
    );
    this.megaMenuWrapper.classList.add("dsc-co-header-nav__megamenu--hidden");

    this.megaMenuDesktop.classList.remove("dsc-co-megaMenu--visible");
    this.megaMenuDesktop.classList.add("dsc-co-megaMenu--hidden");

    this.megaMenuMobile.classList.remove("dsc-co-megaMenu-mobile--visible");
    this.megaMenuMobile.classList.add("dsc-co-megaMenu-mobile--hidden");

    this.megaMenuWrapper.classList.remove("flex", "hidden");

    this.megaMenuDesktop
      .querySelectorAll<HTMLDivElement>(".dsc-co-megaMenu__content")
      .forEach((content, index) => {
        content.classList.remove("flex", "hidden");

        if (index === 0) {
          content.classList.remove("dsc-co-megaMenu__content--hidden");
          content.classList.add("dsc-co-megaMenu__content--visible");
        } else {
          content.classList.remove("dsc-co-megaMenu__content--visible");
          content.classList.add("dsc-co-megaMenu__content--hidden");
        }
      });
  }

  private updateMegaMenuVisibility(isNavbarVisible: boolean): void {
    const uiState = UIState.getInstance();
    const isDesktop = ["lg", "xl"].includes(uiState.currentBreakpoint);

    if (isNavbarVisible && !uiState.isSidebarOpen && isDesktop) {
      this.cleanupMobileContent();
      this.removeMobileEventListeners();

      if (this.isTouchDevice) {
        this.removeDesktopEventListeners();
        this.element.addEventListener("click", this.clickListener);
        document.addEventListener("click", this.documentClickListener);
      } else {
        this.removeDesktopEventListeners();
        this.element.addEventListener("mouseenter", this.mouseEnterListener);
        this.element.addEventListener("mouseleave", this.mouseLeaveListener);
      }
    } else if (!isNavbarVisible || !isDesktop) {
      this.removeDesktopEventListeners();
      this.initializeMobileMenu();
    }
  }

  private removeAllEventListeners(): void {
    this.removeDesktopEventListeners();
    this.removeMobileEventListeners();
  }

  private removeDesktopEventListeners(): void {
    this.element.removeEventListener("mouseenter", this.mouseEnterListener);
    this.element.removeEventListener("mouseleave", this.mouseLeaveListener);
    this.element.removeEventListener("click", this.clickListener);
    document.removeEventListener("click", this.documentClickListener);
  }

  private removeMobileEventListeners(): void {
    this.element.removeEventListener("click", this.mobileTabClickListener);
    this.megaMenuMobile.removeEventListener(
      "click",
      this.mobileMenuClickListener,
    );
  }

  private cleanupMobileContent(): void {
    if (this.currentTeleportedContent) {
      this.returnContentToOriginalPlace();
    }

    if (
      this.megaMenuMobile.classList.contains(
        "dsc-co-megaMenu-mobile--teleported",
      )
    ) {
      this.returnMobileMenuToOriginalPlace();
    }

    this.megaMenuWrapper.classList.add("dsc-co-header-nav__megamenu--hidden");
    this.megaMenuWrapper.classList.remove(
      "dsc-co-header-nav__megamenu--visible",
    );
    this.megaMenuMobile.classList.remove("dsc-co-megaMenu-mobile--visible");
    this.megaMenuMobile.classList.add("dsc-co-megaMenu-mobile--hidden");
    this.megaMenuDesktop.classList.remove("dsc-co-megaMenu--hidden");
    this.megaMenuDesktop.classList.add("dsc-co-megaMenu--visible");
  }

  private updateMegaMenuPosition(isHeaderSticked: boolean): void {
    this.megaMenuWrapper.classList.remove(
      "dsc-co-header-nav__megamenu--sticked",
      "dsc-co-header-nav__megamenu--not-sticked",
    );

    if (isHeaderSticked) {
      this.megaMenuWrapper.classList.add(
        "dsc-co-header-nav__megamenu--sticked",
      );

      if (this.megaMenuWrapper.parentElement !== document.body) {
        document.body.appendChild(this.megaMenuWrapper);
      }
    } else {
      this.megaMenuWrapper.classList.add(
        "dsc-co-header-nav__megamenu--not-sticked",
      );

      if (
        this.headerNavWrapper &&
        this.megaMenuWrapper.parentElement !== this.headerNavWrapper
      ) {
        this.headerNavWrapper.appendChild(this.megaMenuWrapper);
      }
    }
  }

  private showMegaMenu(): void {
    const isNavbarVisible = UIState.getInstance().isNavbarVisible;
    if (!isNavbarVisible) return;

    this.cancelMegaMenuHide();

    this.megaMenuWrapper.classList.remove(
      "dsc-co-header-nav__megamenu--hidden",
    );
    this.megaMenuWrapper.classList.add("dsc-co-header-nav__megamenu--visible");
    this.megaMenuDesktop.classList.add("dsc-co-megaMenu--visible");
    this.megaMenuDesktop.classList.remove("dsc-co-megaMenu--hidden");
    this.megaMenuMobile.classList.add("dsc-co-megaMenu-mobile--hidden");
    this.megaMenuMobile.classList.remove("dsc-co-megaMenu-mobile--visible");

    this.tabLink.classList.add("dsc-co-menu-tab--hover");

    this.megaMenuWrapper.addEventListener(
      "mouseenter",
      this.megaMenuMouseEnterListener,
    );
    this.megaMenuWrapper.addEventListener(
      "mouseleave",
      this.megaMenuMouseLeaveListener,
    );
  }

  private hideMegaMenu(): void {
    const isNavbarVisible = UIState.getInstance().isNavbarVisible;
    if (!isNavbarVisible) return;

    this.megaMenuWrapper.classList.add("dsc-co-header-nav__megamenu--hidden");
    this.megaMenuWrapper.classList.remove(
      "dsc-co-header-nav__megamenu--visible",
    );
    this.megaMenuDesktop.classList.remove("dsc-co-megaMenu--visible");
    this.megaMenuDesktop.classList.add("dsc-co-megaMenu--hidden");
    this.megaMenuMobile.classList.remove("dsc-co-megaMenu-mobile--hidden");
    this.megaMenuMobile.classList.add("dsc-co-megaMenu-mobile--visible");

    this.tabLink.classList.remove("dsc-co-menu-tab--hover");

    this.megaMenuWrapper.removeEventListener(
      "mouseenter",
      this.megaMenuMouseEnterListener,
    );
    this.megaMenuWrapper.removeEventListener(
      "mouseleave",
      this.megaMenuMouseLeaveListener,
    );

    this.cancelMegaMenuHide();
  }

  private scheduleMegaMenuHide(): void {
    this.cancelMegaMenuHide();
    this.hideTimeout = window.setTimeout(() => {
      this.hideMegaMenu();
    }, 50);
  }

  private cancelMegaMenuHide(): void {
    if (this.hideTimeout !== null) {
      window.clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private handleTouchClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const uiState = UIState.getInstance();
    const isNavbarVisible = uiState.isNavbarVisible;
    const isDesktop = ["xl"].includes(uiState.currentBreakpoint);
    if (!isNavbarVisible || !isDesktop || uiState.isSidebarOpen) {
      return;
    }

    if (
      this.megaMenuWrapper.classList.contains(
        "dsc-co-header-nav__megamenu--hidden",
      )
    ) {
      this.closeAllOtherDesktopMenus();

      this.showMegaMenuTouch();
    } else {
      this.hideMegaMenu();
    }
  }

  private handleDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const uiState = UIState.getInstance();
    const isDesktop = ["lg", "xl"].includes(uiState.currentBreakpoint);

    if (!isDesktop || uiState.isSidebarOpen || !uiState.isNavbarVisible) {
      return;
    }

    if (
      this.element.contains(target) ||
      this.megaMenuWrapper.contains(target)
    ) {
      return;
    }

    if (
      !this.megaMenuWrapper.classList.contains(
        "dsc-co-header-nav__megamenu--hidden",
      )
    ) {
      this.hideMegaMenu();
    }
  }

  private showMegaMenuTouch(): void {
    const isNavbarVisible = UIState.getInstance().isNavbarVisible;
    if (!isNavbarVisible) return;

    this.cancelMegaMenuHide();

    this.megaMenuWrapper.classList.remove(
      "dsc-co-header-nav__megamenu--hidden",
    );
    this.megaMenuWrapper.classList.add("dsc-co-header-nav__megamenu--visible");

    this.megaMenuDesktop.classList.remove("dsc-co-megaMenu--hidden");
    this.megaMenuDesktop.classList.add("dsc-co-megaMenu--visible");
    this.megaMenuMobile.classList.remove("dsc-co-megaMenu-mobile--visible");
    this.megaMenuMobile.classList.add("dsc-co-megaMenu-mobile--hidden");
  }

  private handleMegaMenuTabClick(event: MouseEvent, tab: HTMLDivElement): void {
    const tabIndex = tab.getAttribute("data-tab-index");

    this.megaMenuTabsDesktop.forEach((t) => {
      const tabWrapper = t.querySelector(
        ".dsc-co-mega-menu-tab",
      ) as HTMLDivElement;
      const icon = t.querySelector(".dsc-co-mega-menu-tab__icon");

      tabWrapper?.classList.replace(
        "dsc-co-mega-menu-tab--active",
        "dsc-co-mega-menu-tab--default",
      );
      icon?.classList.replace(
        "dsc-co-mega-menu-tab__icon--active",
        "dsc-co-mega-menu-tab__icon--default",
      );
    });

    const tabWrapper = tab.querySelector(".dsc-co-mega-menu-tab");
    tabWrapper?.classList.replace(
      "dsc-co-mega-menu-tab--default",
      "dsc-co-mega-menu-tab--active",
    );

    const icon = tab.querySelector(".dsc-co-mega-menu-tab__icon");
    icon?.classList.replace(
      "dsc-co-mega-menu-tab__icon--default",
      "dsc-co-mega-menu-tab__icon--active",
    );

    this.megaMenuDesktop
      .querySelectorAll<HTMLDivElement>(".dsc-co-megaMenu__content")
      .forEach((content) => {
        content.classList.remove("dsc-co-megaMenu__content--visible");
        content.classList.add("dsc-co-megaMenu__content--hidden");
      });

    const activeContent = this.megaMenuDesktop.querySelector<HTMLDivElement>(
      `.dsc-co-megaMenu__content[data-tab-content="${tabIndex}"]`,
    );
    if (activeContent) {
      activeContent.classList.remove("dsc-co-megaMenu__content--hidden");
      activeContent.classList.add("dsc-co-megaMenu__content--visible");
    }
  }

  private initializeMobileMenu(): void {
    this.removeMobileEventListeners();
    this.element.addEventListener("click", this.mobileTabClickListener);
    this.megaMenuMobile.addEventListener("click", this.mobileMenuClickListener);

    this.mobileInitialized = true;
    const uiState = UIState.getInstance();
    if (!uiState.isSidebarOpen) {
      this.hideMegaMenuMobile();
    }
  }

  private handleMobileMenuClick(event: Event): void {
    const target = event.target as HTMLElement;

    const activeMenuTabLink = target.closest(
      ".dsc-co-menu-tab.dsc-co-menu-tab--active",
    ) as HTMLElement;
    if (activeMenuTabLink) {
      event.preventDefault();
      event.stopPropagation();
      this.hideMegaMenuMobile();
      return;
    }

    const tabElement = target.closest(
      ".dsc-co-megaMenu__tab[data-tab-index]",
    ) as HTMLDivElement;
    if (tabElement) {
      event.stopPropagation();
      this.handleMobileInternalTabClick(event, tabElement);
    }
  }

  private handleMobileInternalTabClick(
    event: Event,
    tab: HTMLDivElement,
  ): void {
    const tabIndex = tab.getAttribute("data-tab-index");

    const targetContent = this.megaMenuMobile.querySelector<HTMLDivElement>(
      `.dsc-co-megaMenu__content[data-tab-content="${tabIndex}"]`,
    );

    if (targetContent) {
      this.teleportContentToWrapper(targetContent, tab);
    }
  }

  private handleMobileTabClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const uiState = UIState.getInstance();

    if (!uiState.isSmallDevice) {
      return;
    }

    this.closeAllOtherMobileMenus();

    if (
      this.megaMenuWrapper.classList.contains(
        "dsc-co-header-nav__megamenu--hidden",
      )
    ) {
      this.showMegaMenuMobile();
    } else {
      this.hideMegaMenuMobile();
    }
  }

  private closeAllOtherMobileMenus(): void {
    const existingTeleportedContent = document.querySelector(
      ".dsc-co-megaMenu-content--teleported",
    );
    if (existingTeleportedContent) {
      existingTeleportedContent.classList.remove("flex", "hidden");

      existingTeleportedContent.classList.remove(
        "dsc-co-megaMenu-content--teleported",
      );
      existingTeleportedContent.classList.remove(
        "dsc-co-megaMenu__content--visible",
      );
      existingTeleportedContent.classList.add(
        "dsc-co-megaMenu__content--hidden",
      );
    }

    const allMegaMenuItems = document.querySelectorAll(
      '[data-plugin="mega-menu"]',
    );

    allMegaMenuItems.forEach((item) => {
      if (item !== this.element) {
        const otherWrapper = item.querySelector(".dsc-co-header-nav__megamenu");
        if (
          otherWrapper &&
          !otherWrapper.classList.contains(
            "dsc-co-header-nav__megamenu--hidden",
          )
        ) {
          otherWrapper.classList.add("dsc-co-header-nav__megamenu--hidden");
          otherWrapper.classList.remove("dsc-co-header-nav__megamenu--visible");
        }
      }
    });
  }

  private closeAllOtherDesktopMenus(): void {
    const allMegaMenuWrappers = document.querySelectorAll(
      ".dsc-co-header-nav__megamenu",
    );

    allMegaMenuWrappers.forEach((wrapper) => {
      const htmlWrapper = wrapper as HTMLElement;

      if (htmlWrapper !== this.megaMenuWrapper) {
        htmlWrapper.classList.add("dsc-co-header-nav__megamenu--hidden");
        htmlWrapper.classList.remove("dsc-co-header-nav__megamenu--visible");
      }
    });
  }

  private showMegaMenuMobile(): void {
    this.teleportMobileMenuToWrapper();

    this.megaMenuWrapper.classList.remove(
      "dsc-co-header-nav__megamenu--hidden",
    );
    this.megaMenuWrapper.classList.add("dsc-co-header-nav__megamenu--visible");
    this.megaMenuMobile.classList.remove("dsc-co-megaMenu-mobile--hidden");
    this.megaMenuMobile.classList.add("dsc-co-megaMenu-mobile--visible");
    this.megaMenuDesktop.classList.add("dsc-co-megaMenu--hidden");
    this.megaMenuDesktop.classList.remove("dsc-co-megaMenu--visible");
  }

  private hideMegaMenuMobile(): void {
    if (this.currentTeleportedContent) {
      this.returnContentToOriginalPlace();
    }

    this.megaMenuWrapper.classList.add("dsc-co-header-nav__megamenu--hidden");
    this.megaMenuWrapper.classList.remove(
      "dsc-co-header-nav__megamenu--visible",
    );
    this.megaMenuMobile.classList.remove("dsc-co-megaMenu-mobile--visible");
    this.megaMenuMobile.classList.add("dsc-co-megaMenu-mobile--hidden");
    this.megaMenuDesktop.classList.remove("dsc-co-megaMenu--hidden");
    this.megaMenuDesktop.classList.add("dsc-co-megaMenu--visible");

    this.returnMobileMenuToOriginalPlace();
  }

  private teleportMobileMenuToWrapper(): void {
    if (!this.headerNavWrapper) {
      console.warn("Header nav wrapper not found, cannot teleport mobile menu");
      return;
    }

    const uiState = UIState.getInstance();
    const isMobile = !["lg", "xl"].includes(uiState.currentBreakpoint);

    this.headerNavWrapper.classList.add("dsc-co-header__nav-wrapper");

    const navUl = this.headerNavWrapper.querySelector("ul");
    if (navUl) {
      navUl.classList.add("hidden");
    }

    if (isMobile) {
      const navBottom = this.headerNavWrapper.querySelector(
        ".dsc-co-header-nav__bottom",
      );
      if (navBottom) {
        navBottom.classList.add("hidden");
      }
    }

    if (this.headerNavWrapper.firstChild) {
      this.headerNavWrapper.insertBefore(
        this.megaMenuMobile,
        this.headerNavWrapper.firstChild,
      );
    } else {
      this.headerNavWrapper.appendChild(this.megaMenuMobile);
    }

    this.megaMenuMobile.classList.add("dsc-co-megaMenu-mobile--teleported");
  }

  private returnMobileMenuToOriginalPlace(): void {
    if (!this.originalParent) {
      console.warn("Original parent not found, cannot return mobile menu");
      return;
    }

    const uiState = UIState.getInstance();
    const isMobile = !["lg", "xl"].includes(uiState.currentBreakpoint);

    this.megaMenuMobile.classList.remove("dsc-co-megaMenu-mobile--teleported");

    if (this.headerNavWrapper) {
      const navUl = this.headerNavWrapper.querySelector("ul");
      if (navUl) {
        navUl.classList.remove("hidden");
      }

      if (isMobile) {
        const navBottom = this.headerNavWrapper.querySelector(
          ".dsc-co-header-nav__bottom",
        );
        if (navBottom) {
          navBottom.classList.remove("hidden");
        }
      }
    }

    if (this.originalNextSibling) {
      this.originalParent.insertBefore(
        this.megaMenuMobile,
        this.originalNextSibling,
      );
    } else {
      this.originalParent.appendChild(this.megaMenuMobile);
    }
  }

  private teleportContentToWrapper(
    content: HTMLElement,
    clickedTab: HTMLDivElement,
  ): void {
    if (!this.headerNavWrapper) {
      console.warn("Header nav wrapper not found, cannot teleport content");
      return;
    }

    if (this.currentTeleportedContent) {
      this.returnCurrentContentToOriginalPlace();
    }

    this.originalContentParent = content.parentElement;
    this.originalContentNextSibling = content.nextElementSibling as HTMLElement;
    this.currentTeleportedContent = content;

    const navUl = this.headerNavWrapper.querySelector("ul");
    if (navUl) {
      navUl.classList.add("hidden");
    }

    const uiState = UIState.getInstance();
    const isMobile = !["lg", "xl"].includes(uiState.currentBreakpoint);

    if (isMobile) {
      const navBottom = this.headerNavWrapper.querySelector(
        ".dsc-co-header-nav__bottom",
      );
      if (navBottom) {
        navBottom.classList.add("hidden");
      }
    }

    if (this.headerNavWrapper.firstChild) {
      this.headerNavWrapper.insertBefore(
        content,
        this.headerNavWrapper.firstChild,
      );
    } else {
      this.headerNavWrapper.appendChild(content);
    }

    content.classList.add(
      "dsc-co-megaMenu-mobile",
      "dsc-co-megaMenu-content--teleported",
    );
    content.classList.remove("dsc-co-megaMenu__content--hidden");
    content.classList.add("dsc-co-megaMenu__content--visible");

    const megaListContainers = content.querySelectorAll(
      ".dsc-co-megaListContainer",
    );
    megaListContainers.forEach((container) => {
      (container as HTMLElement).style.display = "flex";
    });

    this.megaMenuMobile.classList.add("dsc-co-megaMenu-mobile--hidden");
    this.megaMenuMobile.classList.remove("dsc-co-megaMenu-mobile--visible");
    this.megaMenuWrapper.classList.add("dsc-co-header-nav__megamenu--hidden");
    this.megaMenuWrapper.classList.remove(
      "dsc-co-header-nav__megamenu--visible",
    );

    this.createBackNavigationLink(content, clickedTab);

    content.addEventListener("click", (event) => {
      this.handleTeleportedContentClick(event);
    });
  }

  private returnContentToOriginalPlace(): void {
    if (!this.currentTeleportedContent || !this.originalContentParent) {
      return;
    }

    const backNavigation = this.currentTeleportedContent.querySelector(
      ".dsc-co-back-navigation",
    );
    if (backNavigation) {
      backNavigation.remove();
    }

    this.currentTeleportedContent.classList.remove(
      "dsc-co-megaMenu-content--teleported",
    );

    if (this.headerNavWrapper) {
      const navUl = this.headerNavWrapper.querySelector("ul");
      if (navUl) {
        navUl.classList.remove("hidden");
      }

      // Solo manipular navBottom si estamos realmente en modo móvil
      const uiState = UIState.getInstance();
      const isMobile = !["lg", "xl"].includes(uiState.currentBreakpoint);

      if (isMobile) {
        const navBottom = this.headerNavWrapper.querySelector(
          ".dsc-co-header-nav__bottom",
        );
        if (navBottom) {
          navBottom.classList.remove("hidden");
        }
      }
    }

    if (this.originalContentNextSibling) {
      this.originalContentParent.insertBefore(
        this.currentTeleportedContent,
        this.originalContentNextSibling,
      );
    } else {
      this.originalContentParent.appendChild(this.currentTeleportedContent);
    }

    this.currentTeleportedContent.classList.remove(
      "dsc-co-megaMenu__content--visible",
    );
    this.currentTeleportedContent.classList.add(
      "dsc-co-megaMenu__content--hidden",
    );

    const megaListContainers = this.currentTeleportedContent.querySelectorAll(
      ".dsc-co-megaListContainer",
    );
    megaListContainers.forEach((container) => {
      (container as HTMLElement).style.display = "none";
    });

    this.megaMenuMobile.classList.remove("dsc-co-megaMenu-mobile--hidden");
    this.megaMenuMobile.classList.add("dsc-co-megaMenu-mobile--visible");

    const tabs = this.megaMenuMobile.querySelector(".dsc-co-megaMenu__tabs");
    if (tabs) {
      tabs.classList.remove("hidden");
    }

    this.currentTeleportedContent = null;
    this.originalContentParent = null;
    this.originalContentNextSibling = null;
  }

  private returnCurrentContentToOriginalPlace(): void {
    if (!this.currentTeleportedContent || !this.originalContentParent) {
      return;
    }

    const backNavigation = this.currentTeleportedContent.querySelector(
      ".dsc-co-back-navigation",
    );
    if (backNavigation) {
      backNavigation.remove();
    }

    this.currentTeleportedContent.classList.remove(
      "dsc-co-megaMenu-content--teleported",
    );

    if (this.originalContentNextSibling) {
      this.originalContentParent.insertBefore(
        this.currentTeleportedContent,
        this.originalContentNextSibling,
      );
    } else {
      this.originalContentParent.appendChild(this.currentTeleportedContent);
    }

    this.currentTeleportedContent.classList.remove(
      "dsc-co-megaMenu__content--visible",
    );
    this.currentTeleportedContent.classList.add(
      "dsc-co-megaMenu__content--hidden",
    );

    const megaListContainers = this.currentTeleportedContent.querySelectorAll(
      ".dsc-co-megaListContainer",
    );
    megaListContainers.forEach((container) => {
      (container as HTMLElement).style.display = "none";
    });

    this.currentTeleportedContent = null;
    this.originalContentParent = null;
    this.originalContentNextSibling = null;
  }

  private createBackNavigationLink(
    content: HTMLElement,
    clickedTab: HTMLDivElement,
  ): void {
    const existingBackLink = content.querySelector(".dsc-co-back-navigation");
    if (existingBackLink) {
      existingBackLink.remove();
    }

    const tabLabel =
      clickedTab.querySelector(".dsc-co-menu-tab__label")?.textContent ||
      "Atrás";

    const backNavigationHtml = `
      <div class="dsc-co-back-navigation">
        <a href="#" class="dsc-co-menu-tab dsc-co-menu-tab--active dsc-co-menu-tab--sm">
          <span class="dsc-co-menu-tab__label">${tabLabel}</span>
          <i class="dsc-co-icon dsc-co-icon-chevronright dsc-co-menu-tab__icon dsc-co-menu-tab__icon--trailing"></i>
        </a>
      </div>
    `;

    content.insertAdjacentHTML("afterbegin", backNavigationHtml);

    const backLink = content.querySelector(
      ".dsc-co-back-navigation .dsc-co-menu-tab",
    );
    if (backLink) {
      backLink.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.returnContentToOriginalPlace();
      });
    }
  }

  private handleTeleportedContentClick(event: Event): void {
    const target = event.target as HTMLElement;

    const backLink = target.closest(".dsc-co-menu-tab.dsc-co-menu-tab--active");
    if (backLink) {
      event.preventDefault();
      event.stopPropagation();
      this.returnContentToOriginalPlace();
    }
  }

  destroy(): void {
    if (this.unsubscribeNavbar) {
      this.unsubscribeNavbar();
      this.unsubscribeNavbar = undefined;
    }

    if (this.unsubscribeHeaderSticked) {
      this.unsubscribeHeaderSticked();
      this.unsubscribeHeaderSticked = undefined;
    }

    if (this.unsubscribeBreakpoint) {
      this.unsubscribeBreakpoint();
      this.unsubscribeBreakpoint = undefined;
    }

    this.cancelMegaMenuHide();

    this.removeAllEventListeners();

    this.megaMenuWrapper.removeEventListener(
      "mouseenter",
      this.megaMenuMouseEnterListener,
    );
    this.megaMenuWrapper.removeEventListener(
      "mouseleave",
      this.megaMenuMouseLeaveListener,
    );

    if (
      this.megaMenuOriginalParent &&
      this.megaMenuWrapper.parentElement !== this.megaMenuOriginalParent
    ) {
      if (this.megaMenuOriginalNextSibling) {
        this.megaMenuOriginalParent.insertBefore(
          this.megaMenuWrapper,
          this.megaMenuOriginalNextSibling,
        );
      } else {
        this.megaMenuOriginalParent.appendChild(this.megaMenuWrapper);
      }
    }

    if (this.currentTeleportedContent) {
      this.returnContentToOriginalPlace();
    }

    if (
      this.megaMenuMobile?.classList.contains(
        "dsc-co-megaMenu-mobile--teleported",
      )
    ) {
      this.returnMobileMenuToOriginalPlace();
    }
  }
}

PluginRegistry.register("mega-menu", MegaMenu);
